// 📄 pages/SAP-BTP/components/AplicarPerfilModal.tsx
// "Aplicar perfil" (docs/plans/plan-perfiles-valores.md, P3): con las reglas
// "Reemplaza a -> Valor" de un perfil, propone los cambios sobre los valores
// del iFlow (vista previa, antes -> después, cada uno se puede desmarcar) y
// los entrega como cambios; quien llama decide (formulario o pendientes del
// diagrama). Nada se guarda acá.
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, KeyRound, RefreshCw, X } from 'lucide-react';
import { CambioPorPerfil, PerfilValores, ResumenPerfil, calcularAplicacion, listarPerfiles } from '../../../services/perfilValoresService.ts';
import { cargarPerfil, recordarPerfilUsado, ultimoPerfilUsado } from './SelectorPerfil.tsx';

interface AplicarPerfilModalProps {
  orgId: string;
  // Valores del iFlow (con lo ya cambiado) y cómo mostrarlos.
  valores: { clave: string; valor: string; etiqueta: string }[];
  onAplicar: (cambios: Record<string, string>) => void;
  onCerrar: () => void;
}

export default function AplicarPerfilModal({ orgId, valores, onAplicar, onCerrar }: AplicarPerfilModalProps) {
  const [perfiles, setPerfiles] = useState<ResumenPerfil[] | null>(null);
  const [perfilId, setPerfilId] = useState('');
  const [perfil, setPerfil] = useState<PerfilValores | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [excluidos, setExcluidos] = useState<Set<string>>(new Set());

  useEffect(() => {
    listarPerfiles(orgId)
      .then((lista) => {
        setPerfiles(lista);
        const ultimo = ultimoPerfilUsado(orgId);
        setPerfilId(lista.some((p) => p.id === ultimo) ? ultimo : lista[0]?.id || '');
      })
      .catch((err) => { setError(err?.message || 'No se pudieron cargar los perfiles.'); setPerfiles([]); });
  }, [orgId]);

  useEffect(() => {
    if (!perfilId) { setPerfil(null); return; }
    let vigente = true;
    setCargando(true);
    setError(null);
    setExcluidos(new Set());
    cargarPerfil(orgId, perfilId)
      .then((p) => { if (vigente) setPerfil(p); })
      .catch((err) => { if (vigente) setError(err?.message || 'No se pudo abrir el perfil.'); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [orgId, perfilId]);

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onCerrar(); } };
    window.addEventListener('keydown', alTeclear, true);
    return () => window.removeEventListener('keydown', alTeclear, true);
  }, [onCerrar]);

  const cambios: CambioPorPerfil[] = useMemo(() => (perfil ? calcularAplicacion(valores, perfil) : []), [perfil, valores]);
  const etiquetas = useMemo(() => new Map(valores.map((v) => [v.clave, v.etiqueta])), [valores]);
  const reglas = perfil?.valores.filter((v) => v.reemplaza).length ?? 0;
  const elegidos = cambios.filter((c) => !excluidos.has(c.clave));

  const aplicar = () => {
    if (perfilId) recordarPerfilUsado(orgId, perfilId);
    onAplicar(Object.fromEntries(elegidos.map((c) => [c.clave, c.despues])));
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onCerrar}>
      <div className="bg-gray-800 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
          <div>
            <h3 className="text-sm font-semibold text-white flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-400" /> Aplicar un perfil</h3>
            <p className="text-[11px] text-gray-400 mt-1">
              Cada valor del perfil con "Reemplaza a" busca ese texto dentro de los valores del iFlow y lo cambia (ej. el host dentro de una URL completa).
            </p>
          </div>
          <button onClick={onCerrar} className="text-gray-400 hover:text-white" aria-label="Cerrar"><X className="w-4 h-4" /></button>
        </header>

        {perfiles === null ? (
          <div className="p-8 text-center text-gray-400"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : perfiles.length === 0 ? (
          <p className="p-6 text-sm text-gray-400 text-center">No tienes perfiles en esta organización. Créalos en el Repositorio, con "Perfiles de valores".</p>
        ) : (
          <>
            <div className="p-4 border-b border-white/10">
              <select value={perfilId} onChange={(e) => setPerfilId(e.target.value)} aria-label="Perfil"
                className="bg-zinc-800 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 w-full sm:w-72">
                {perfiles.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.ambiente ? ` (${p.ambiente})` : ''}</option>)}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {error && <p className="text-xs text-red-400">{error}</p>}
              {cargando ? (
                <div className="p-6 text-center text-gray-400"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
              ) : !perfil ? null : reglas === 0 ? (
                <p className="text-xs text-gray-400 text-center p-4">Este perfil no tiene valores con "Reemplaza a". Agrégalos en "Perfiles de valores" para poder aplicarlo de golpe.</p>
              ) : cambios.length === 0 ? (
                <p className="text-xs text-gray-400 text-center p-4">Ninguna regla de este perfil coincide con los valores de este iFlow.</p>
              ) : (
                <>
                  <p className="text-xs text-gray-400">{cambios.length} valor{cambios.length === 1 ? '' : 'es'} cambiaría{cambios.length === 1 ? '' : 'n'}. Desmarca los que no quieras.</p>
                  {cambios.map((c) => (
                    <label key={c.clave} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${excluidos.has(c.clave) ? 'border-white/10 opacity-50' : 'border-cyan-500/30 bg-cyan-500/5'}`}>
                      <input type="checkbox" className="mt-1" checked={!excluidos.has(c.clave)}
                        onChange={(e) => setExcluidos((x) => { const n = new Set(x); if (e.target.checked) n.delete(c.clave); else n.add(c.clave); return n; })} />
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-xs text-white">{etiquetas.get(c.clave) || c.clave} <span className="text-gray-500">· {c.reglas.join(', ')}</span></p>
                        <p className="text-[11px] font-mono text-gray-400 break-all line-through decoration-gray-600">{c.antes}</p>
                        <p className="text-[11px] font-mono text-cyan-300 break-all flex items-start gap-1"><ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />{c.despues}</p>
                      </div>
                    </label>
                  ))}
                </>
              )}
            </div>

            <footer className="flex justify-end gap-2 p-4 border-t border-white/10">
              <button onClick={onCerrar} className="px-4 py-2 text-sm text-gray-300 hover:text-white">Cancelar</button>
              <button onClick={aplicar} disabled={!elegidos.length}
                className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40">
                Aplicar {elegidos.length || ''} cambio{elegidos.length === 1 ? '' : 's'}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
